import { toTimestamp } from '../utils/addWebSocketToReplication'
import { toISOString, toISOStringOrNull } from '../utils/pushSafetyUtils'
import { createReplication } from './createReplication'
import type { SavedFilterCollection } from '../index'

const SCOPE_MAP: Record<number, 'project' | 'space' | 'global'> = {
  0: 'project',
  1: 'space',
  2: 'global',
}

const SCOPE_INT_MAP: Record<string, number> = {
  project: 0,
  space: 1,
  global: 2,
}

const PERMISSION_MAP: Record<number, 'private' | 'workspace' | 'project'> = {
  0: 'private',
  1: 'workspace',
  2: 'project',
}

const PERMISSION_INT_MAP: Record<string, number> = {
  private: 0,
  workspace: 1,
  project: 2,
}

function toScopeString(v: number | string | null | undefined): 'project' | 'space' | 'global' {
  if (typeof v === 'string') return (SCOPE_MAP[SCOPE_INT_MAP[v] ?? 0] ?? 'project')
  return SCOPE_MAP[v ?? 0] ?? 'project'
}

function fromScopeString(v: string | null | undefined): number {
  return SCOPE_INT_MAP[v ?? 'project'] ?? 0
}

function toPermissionString(v: number | string | null | undefined): 'private' | 'workspace' | 'project' {
  if (typeof v === 'string') return (PERMISSION_MAP[PERMISSION_INT_MAP[v] ?? 0] ?? 'private')
  return PERMISSION_MAP[v ?? 0] ?? 'private'
}

function fromPermissionString(v: string | null | undefined): number {
  return PERMISSION_INT_MAP[v ?? 'private'] ?? 0
}

export const setupSavedFilterReplication = (
  collection: SavedFilterCollection,
  envId: string,
) =>
  createReplication({
    entityName: 'savedfilters',
    displayName: 'SavedFilter',
    collection,
    envId,
    mapFromServer: (doc) => ({
      id: doc.id,
      envId: doc.envId,
      name: doc.name,
      description: doc.description || null,
      filterConfig: doc.filterConfig,
      layoutConfig: doc.layoutConfig ?? null,
      scope: toScopeString(doc.scope),
      scopeEntityId: doc.scopeEntityId || null,
      permission: toPermissionString(doc.permission),
      ownerId: doc.ownerId,
      icon: doc.icon || null,
      sortOrder: doc.sortOrder || null,
      isDeleted: doc.isDeleted || false,
      createdOn: toTimestamp(doc.createdOn) ?? new Date(doc.createdOn).getTime(),
      createdBy: doc.createdBy,
      updatedOn: toTimestamp(doc.updatedOn) ?? new Date(doc.updatedOn).getTime(),
      updatedBy: doc.updatedBy,
      deletedOn: doc.deletedOn ? (toTimestamp(doc.deletedOn) ?? new Date(doc.deletedOn).getTime()) : null,
      deletedBy: doc.deletedBy,
    }),
    mapToServer: (state, envId) => ({
      id: state.id,
      envId,
      name: state.name,
      description: state.description || null,
      filterConfig: state.filterConfig,
      layoutConfig: state.layoutConfig ?? null,
      scope: fromScopeString(state.scope),
      scopeEntityId: state.scopeEntityId || null,
      permission: fromPermissionString(state.permission),
      ownerId: state.ownerId,
      icon: state.icon || null,
      sortOrder: state.sortOrder || null,
      isDeleted: state.isDeleted,
      createdOn: toISOString(state.createdOn),
      createdBy: state.createdBy,
      updatedOn: toISOString(state.updatedOn),
      updatedBy: state.updatedBy,
      deletedOn: toISOStringOrNull(state.deletedOn),
      deletedBy: state.deletedBy,
    }),
  })
